/* ==========================================================================
   MEE BARSA — Wishlist (client-side, no app, no metafields)
   Storage: localStorage array of product handles under key "mb_wishlist".
   Why localStorage instead of a wishlist app or customer metafields:
   see KNOWLEDGE_TRANSFER.md session log — Shopify has no native wishlist,
   Part 7 says "no unnecessary third-party apps", and metafield/customer-account
   writes need an app or a backend proxy to call the Admin API. localStorage
   needs neither, works for guests (Anjali's customers mostly won't have
   accounts yet), and every product card/page just needs a data-handle + this
   one script. Trade-off: wishlist is per-browser, not synced across devices,
   and clears if the customer clears site data — acceptable for a v1 launch.
   ========================================================================== */
(function () {
  // guard: this file is included on multiple sections (hero, new-arrivals, wishlist
  // page) so the same product can show a heart button in more than one place —
  // only wire up listeners/init once per page load.
  if (window.__mbWishlistInit) return;
  window.__mbWishlistInit = true;

  var STORAGE_KEY = 'mb_wishlist';
  var PAGE_URL = (window.mbWishlistPageUrl || '/pages/wishlist');

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) { /* storage unavailable — fail silently, buttons just won't persist */ }
    document.dispatchEvent(new CustomEvent('mb:wishlist:change', { detail: { list: list } }));
  }

  function isSaved(handle) {
    return read().indexOf(handle) > -1;
  }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i > -1) { list.splice(i, 1); } else { list.unshift(handle); }
    write(list);
    return list.indexOf(handle) > -1;
  }

  function remove(handle) {
    var list = read().filter(function (h) { return h !== handle; });
    write(list);
  }

  function syncButtons() {
    var list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-wishlist-toggle');
      btn.classList.toggle('is-saved', list.indexOf(handle) > -1);
      btn.setAttribute('aria-pressed', list.indexOf(handle) > -1 ? 'true' : 'false');
    });
    var countEls = document.querySelectorAll('[data-wishlist-count]');
    countEls.forEach(function (el) {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  }

  function wireButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-wishlist-toggle');
      if (!handle) return;
      toggle(handle);
      syncButtons();
    });
  }

  // Product titles are Anjali's to write, so this is hygiene rather than a
  // live XSS hole — but a stray quote or ampersand in a piece name used to
  // break the card markup outright. Escape everything that reaches innerHTML.
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function cardTemplate(product) {
    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    return (
      '<div class="mb-wl-card" data-wl-handle="' + esc(product.handle) + '">' +
        '<a class="mb-card" href="' + esc(product.url) + '">' +
          '<div class="mb-frame">' +
            (img ? '<img src="' + esc(img) + '" alt="' + esc(product.title) + '" loading="lazy">' : '') +
          '</div>' +
          '<span class="mb-cname">' + esc(product.title) + '</span>' +
        '</a>' +
        '<button type="button" class="mb-wl-remove" data-wishlist-toggle="' + esc(product.handle) + '" aria-label="Remove from wishlist">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>' +
          '<span>Remove</span>' +
        '</button>' +
      '</div>'
    );
  }

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* PRUNING — added 2026-08-02 after regression testing.
     Saved handles used to be fetched and then quietly dropped with
     .filter(Boolean) if the product had been deleted or unpublished. That is
     not hypothetical: seven pieces were moved to draft on 2 Aug because they
     had no price. Anyone who had saved one watched their wishlist shrink with
     no explanation, and the dead handle stayed in localStorage for good — so
     it failed again on every future visit. If every saved piece had gone, they
     got the empty state and would reasonably conclude the site lost their data.
     Now: drop the handle from storage, and say one quiet line about it. */
  function pruneMissing(list, products) {
    var alive = [];
    var lost = 0;
    products.forEach(function (p, i) {
      if (p) { alive.push(list[i]); } else { lost++; }
    });
    if (lost > 0) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(alive));
      } catch (e) { /* storage unavailable — nothing to prune to */ }
      syncButtons();
    }
    return lost;
  }

  function renderNotice(container, lost) {
    if (!lost) return;
    var p = document.createElement('p');
    p.className = 'mb-wl-notice';
    p.setAttribute('role', 'status');
    p.textContent = lost === 1
      ? 'One saved piece is no longer available.'
      : lost + ' saved pieces are no longer available.';
    container.parentNode.insertBefore(p, container);
  }

  function renderMiniPreview(container) {
    if (!container) return;
    var list = read();
    if (list.length === 0) {
      container.innerHTML = '<p class="mb-wishlist-empty">Your wishlist is empty</p>';
      return;
    }
    var shown = list.slice(0, 4);
    Promise.all(shown.map(fetchProduct)).then(function (products) {
      var rows = products.filter(Boolean).map(function (p) {
        var img = p.featured_image;
        if (img && img.indexOf('//') === 0) img = 'https:' + img;
        return (
          '<a class="mb-wl-mini-row" href="' + p.url + '">' +
            (img ? '<img src="' + img + '" alt="">' : '<span class="mb-wl-mini-swatch"></span>') +
            '<span>' + p.title + '</span>' +
          '</a>'
        );
      }).join('');
      var more = list.length > shown.length ? '<p class="mb-wl-mini-more">+' + (list.length - shown.length) + ' more</p>' : '';
      container.innerHTML =
        rows +
        more +
        '<a class="mb-wl-view-all" href="' + PAGE_URL + '">View full wishlist</a>';
    });
  }

  function renderFullPage(container, emptyMsgEl) {
    if (!container) return;
    var list = read();
    if (list.length === 0) {
      container.innerHTML = '';
      if (emptyMsgEl) emptyMsgEl.hidden = false;
      return;
    }
    if (emptyMsgEl) emptyMsgEl.hidden = true;

    // Clear any notice from a previous render so it can't accumulate.
    var old = container.parentNode.querySelector('.mb-wl-notice');
    if (old) old.parentNode.removeChild(old);

    Promise.all(list.map(fetchProduct)).then(function (products) {
      var lost = pruneMissing(list, products);
      var alive = products.filter(Boolean);
      container.innerHTML = alive.map(cardTemplate).join('');
      renderNotice(container, lost);
      // Everything they had saved is gone — say so, rather than showing the
      // "your wishlist is empty" state, which reads as lost data.
      if (alive.length === 0 && emptyMsgEl) emptyMsgEl.hidden = false;
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    wireButtons();
    syncButtons();

    document.querySelectorAll('[data-wishlist-mini]').forEach(function (el) {
      renderMiniPreview(el);
    });

    var fullPageContainer = document.querySelector('[data-wishlist-grid]');
    var emptyMsgEl = document.querySelector('[data-wishlist-empty-msg]');
    renderFullPage(fullPageContainer, emptyMsgEl);

    /* Header heart icon. Hover still shows the mini preview on desktop (CSS
       :hover on .mb-icon-btn) — that stays as the bonus.
       SINGLE CLICK opens the full page, changed 2026-08-02. It was dblclick,
       which nobody discovers, no keyboard user can reach, and which barely
       exists on a phone — where most of Anjali's customers are. The preview is
       an enhancement; the link is the contract. */
    document.querySelectorAll('[data-wishlist-icon]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.href = PAGE_URL;
      });
      // Anything focusable should answer Enter/Space the same way.
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          window.location.href = PAGE_URL;
        }
      });
    });

    document.addEventListener('mb:wishlist:change', function () {
      document.querySelectorAll('[data-wishlist-mini]').forEach(renderMiniPreview);
      if (fullPageContainer) renderFullPage(fullPageContainer, emptyMsgEl);
    });
  });

  window.mbWishlist = { get: read, isSaved: isSaved, toggle: toggle, remove: remove };
})();
